import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./auth";
import { REPORT_ITEM_LIMIT, getAllowedOrientations, getReportTypeLabel, supportsOrientationToggle } from "./reports/reportTypes";
import { validateReportItemAccess } from "./reports/permissions";
import { downloadReportBlob, exportReportWorkspacePdf } from "./reports/reportWorkspaceApi";
import { useReportWorkspace } from "./reports/useReportWorkspace";
import ReportPreview from "./reports/renderers/ReportPreview";
import "./reports/styles.css";

function OrientationSelect({ item, disabled, onChange }) {
  const orientations = getAllowedOrientations(item.type);
  if (!supportsOrientationToggle(item.type)) {
    return <span className="reports-badge">{item.orientation === "landscape" ? "Na šířku" : "Na výšku"}</span>;
  }
  return (
    <select
      className="reports-chip-button"
      value={item.orientation}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      aria-label="Orientace reportu"
    >
      {orientations.map((orientation) => (
        <option key={orientation} value={orientation}>
          {orientation === "landscape" ? "Na šířku" : "Na výšku"}
        </option>
      ))}
    </select>
  );
}

function ReportPreviewModal({ item, onClose }) {
  if (!item) return null;
  return (
    <div className="reports-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className="reports-modal" style={{ width: "min(980px, 100%)" }} role="dialog" aria-modal="true" aria-label="Náhled reportu" onMouseDown={(event) => event.stopPropagation()}>
        <div className="reports-modal-header">
          <div>
            <div className="reports-eyebrow">Náhled PDF stránky</div>
            <h2>{item.title}</h2>
          </div>
          <button type="button" className="reports-icon-button" onClick={onClose} aria-label="Zavřít">x</button>
        </div>
        <ReportPreview item={item} mode="full" />
      </div>
    </div>
  );
}

function ReportCard({ item, index, total, access, onOpen, onUpdate, onDelete, onMove }) {
  const disabled = !access.ok;
  const [draft, setDraft] = useState({ title: item.title, note: item.note });
  const draftRef = useRef(draft);
  const dirtyRef = useRef({ title: false, note: false });
  const saveVersionRef = useRef({ title: 0, note: 0 });
  const itemIdRef = useRef(item.id);

  useEffect(() => {
    if (itemIdRef.current !== item.id) {
      itemIdRef.current = item.id;
      dirtyRef.current = { title: false, note: false };
      const next = { title: item.title, note: item.note };
      draftRef.current = next;
      setDraft(next);
      return;
    }
    setDraft((current) => {
      const next = {
        title: dirtyRef.current.title ? current.title : item.title,
        note: dirtyRef.current.note ? current.note : item.note,
      };
      draftRef.current = next;
      return next;
    });
  }, [item.id, item.title, item.note]);

  const changeDraft = (field, value) => {
    dirtyRef.current[field] = true;
    const next = { ...draftRef.current, [field]: value };
    draftRef.current = next;
    setDraft(next);
  };

  const saveDraft = async (field) => {
    if (!dirtyRef.current[field]) return;
    const value = draftRef.current[field];
    const version = saveVersionRef.current[field] + 1;
    saveVersionRef.current[field] = version;
    try {
      const saved = await onUpdate(item.id, { [field]: value });
      if (saveVersionRef.current[field] !== version || draftRef.current[field] !== value) return;
      dirtyRef.current[field] = false;
      const nextValue = saved && saved[field] !== undefined ? String(saved[field]) : value;
      const next = { ...draftRef.current, [field]: nextValue };
      draftRef.current = next;
      setDraft(next);
    } catch (_) {
      // Keep the unsaved draft visible so blur or a later edit can retry it.
    }
  };

  const saveImmediate = (patch) => {
    Promise.resolve(onUpdate(item.id, patch)).catch(() => {});
  };

  return (
    <article className={`reports-card${disabled ? " is-invalid" : ""}`}>
      <ReportPreview item={item} mode="thumbnail" />
      <div className="reports-card-meta">
        <span className="reports-badge">{getReportTypeLabel(item.type)}</span>
        <span className="reports-badge">{item.orientation === "landscape" ? "Na šířku" : "Na výšku"}</span>
      </div>
      {disabled ? <div className="reports-invalid-banner">{access.reason}</div> : null}
      <input
        className="reports-card-title"
        value={draft.title}
        onChange={(event) => changeDraft("title", event.target.value)}
        onBlur={() => saveDraft("title")}
        aria-label="Název položky"
      />
      <textarea
        className="reports-card-note"
        value={draft.note}
        onChange={(event) => changeDraft("note", event.target.value)}
        onBlur={() => saveDraft("note")}
        placeholder="Popisek"
        aria-label="Popisek položky"
      />
      <div className="reports-card-footer">
        <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontWeight: 800, color: "#334155" }}>
          <input
            type="checkbox"
            checked={item.includeInExport}
            onChange={(event) => saveImmediate({ includeInExport: event.target.checked })}
          />
          Zahrnout
        </label>
        <OrientationSelect item={item} disabled={disabled} onChange={(orientation) => saveImmediate({ orientation })} />
      </div>
      <div className="reports-card-actions">
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span className="reports-badge" title="Reorder handle">::</span>
          <button type="button" className="reports-chip-button" onClick={() => onMove(item.id, "up")} disabled={index === 0}>
            Nahoru
          </button>
          <button type="button" className="reports-chip-button" onClick={() => onMove(item.id, "down")} disabled={index === total - 1}>
            Dolů
          </button>
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <button type="button" className="reports-secondary-button" onClick={() => onOpen(item)} disabled={disabled}>
            Náhled
          </button>
          <button type="button" className="reports-danger-button" onClick={() => onDelete(item.id)}>
            Smazat
          </button>
        </div>
      </div>
    </article>
  );
}

export default function ReportsPage() {
  const auth = useAuth();
  const {
    workspaces,
    workspace,
    status,
    saveStatus,
    error,
    createWorkspace,
    openWorkspace,
    renameWorkspace,
    deleteWorkspace,
    updateItem,
    deleteItem,
    moveItem,
  } = useReportWorkspace(auth.user?.id, auth.currentAreaId);
  const [previewItemId, setPreviewItemId] = useState("");
  const [exportState, setExportState] = useState({ status: "idle", kind: "", message: "" });
  const [titleDraft, setTitleDraft] = useState("");
  const items = workspace.items;

  useEffect(() => {
    setTitleDraft(workspace.title || "");
  }, [workspace.id, workspace.title]);

  const accessById = useMemo(() => {
    const map = new Map();
    items.forEach((item) => map.set(item.id, validateReportItemAccess(item, auth)));
    return map;
  }, [auth, items]);
  const previewItem = items.find((item) => item.id === previewItemId) || null;
  const previewAccess = previewItem ? accessById.get(previewItem.id) : null;
  const includedItems = items.filter((item) => item.includeInExport);
  const invalidIncludedItems = includedItems.filter((item) => !accessById.get(item.id)?.ok);
  const exportableItems = includedItems.filter((item) => accessById.get(item.id)?.ok);
  const includedCount = exportableItems.length;
  const isExporting = exportState.status === "loading";
  const canExport = Boolean(workspace.id) && exportableItems.length > 0 && invalidIncludedItems.length === 0 && !isExporting;

  const createNewReport = async () => {
    await createWorkspace({
      title: `ElectionMap report${auth.currentAreaName ? ` - ${auth.currentAreaName}` : ""}`,
    });
  };

  const saveTitle = async () => {
    if (!workspace.id) return;
    await renameWorkspace({ title: titleDraft });
  };

  const runPdfExport = async () => {
    if (!canExport) return;
    setExportState({ status: "loading", kind: "", message: "Generuji PDF..." });
    try {
      const result = await exportReportWorkspacePdf(auth.currentAreaId, workspace.id);
      downloadReportBlob(result.blob, result.filename);
      setExportState({ status: "success", kind: "", message: "PDF bylo vygenerováno a stahování začalo." });
    } catch (error) {
      const isValidation = error && error.kind === "validation";
      setExportState({
        status: "error",
        kind: isValidation ? "validation" : "runtime",
        message: isValidation
          ? `Export nelze vygenerovat: ${error.message || "zkontrolujte oprávnění a položky reportu."}`
          : `Exportér dočasně selhal: ${error.message || "zkuste to prosím znovu."}`,
      });
    }
  };

  return (
    <div className="reports-page">
      <header className="reports-page-header">
        <div className="reports-workspace-controls">
          <select
            className="reports-workspace-select"
            value={workspace.id || ""}
            onChange={(event) => openWorkspace(event.target.value)}
            disabled={status === "loading" || workspaces.length === 0}
            aria-label="Vybrat uložený report"
          >
            {workspaces.length === 0 ? <option value="">Žádný uložený report</option> : null}
            {workspaces.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.title || `Report ${candidate.id}`}
              </option>
            ))}
          </select>
          <button type="button" className="reports-secondary-button" onClick={createNewReport}>
            Nový report
          </button>
          <input
            className="reports-workspace-title"
            value={titleDraft}
            onChange={(event) => setTitleDraft(event.target.value)}
            disabled={!workspace.id}
            aria-label="Název reportu"
          />
          <button type="button" className="reports-chip-button" onClick={saveTitle} disabled={!workspace.id || saveStatus === "saving"}>
            Uložit název
          </button>
          <button type="button" className="reports-danger-button" onClick={deleteWorkspace} disabled={!workspace.id || saveStatus === "saving"}>
            Smazat report
          </button>
          <span className="reports-badge">{saveStatus === "saving" ? "Ukládám" : saveStatus === "saved" ? "Uloženo" : status === "loading" ? "Načítám" : "DB"}</span>
        </div>
        <div className="reports-toolbar">
          <span className="reports-badge">{items.length}/{REPORT_ITEM_LIMIT} položek</span>
          <span className="reports-badge">{includedCount} zahrnuto</span>
          <button
            type="button"
            className="reports-primary-button"
            disabled={!canExport}
            onClick={runPdfExport}
            title={
              invalidIncludedItems.length > 0
                ? "Některé zahrnuté položky už nejsou pro aktuální oprávnění dostupné."
                : exportableItems.length === 0
                  ? "Vyberte alespoň jednu dostupnou položku."
                  : "Vygenerovat PDF"
            }
          >
            {isExporting ? "Generuji PDF..." : "Vygenerovat PDF"}
          </button>
        </div>
      </header>
      {invalidIncludedItems.length > 0 ? (
        <div className="reports-export-message is-validation">
          Export je zablokovaný, protože některé zahrnuté položky už nejsou dostupné pro aktuální oprávnění nebo aktuální oblast.
        </div>
      ) : null}
      {error ? <div className="reports-export-message is-error">{error}</div> : null}
      {exportState.message ? (
        <div className={`reports-export-message is-${exportState.status}${exportState.kind ? ` is-${exportState.kind}` : ""}`}>
          {exportState.message}
        </div>
      ) : null}

      {items.length === 0 ? (
        <div className="reports-empty">
          Zatím tu není žádná položka. Uložte mapu z modulů Mapa, Tsunami nebo Místa tlačítkem “Uložit do reportu”.
        </div>
      ) : (
        <section className="reports-grid" aria-label="Uložené položky reportu">
          {items.map((item, index) => (
            <ReportCard
              key={item.id}
              item={item}
              index={index}
              total={items.length}
              access={accessById.get(item.id) || { ok: false, reason: "Položku se nepodařilo ověřit." }}
              onUpdate={updateItem}
              onDelete={deleteItem}
              onMove={moveItem}
              onOpen={(candidate) => {
                const candidateAccess = accessById.get(candidate.id);
                if (candidateAccess && candidateAccess.ok) setPreviewItemId(candidate.id);
              }}
            />
          ))}
        </section>
      )}

      {previewItem && previewAccess?.ok ? <ReportPreviewModal item={previewItem} onClose={() => setPreviewItemId("")} /> : null}
    </div>
  );
}
